/**
 * ENG-4644: Local-first order state service.
 *
 * Holds the client-side order state and applies mutations optimistically.
 * UI components subscribe to `state$` for instant updates.
 * The SyncQueueService handles background server sync and reconciliation.
 */
import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Subscription } from 'rxjs';
import { debounceTime, skip } from 'rxjs/operators';
import { cloneDeep } from 'lodash';
import { OrderHelpers } from '../_helpers/helpers';
import { posSyncDb } from './pos-sync-db';

export interface LocalOrderItem {
  id: number | string;     // real server ID or "local-<uuid>"
  menu_item: any;
  menu_item_raw: string;
  quantity: number;
  modifiers: any[];
  special_request: string;
  price: string;
  is_removed: boolean;
  is_updated: boolean;
  ts_submitted: boolean;
  sale_price: any;
  portion: string;
  /** Seat numbers: 0 = Table, 1..N = guest, null = unassigned; "2,5" when shared. */
  seat?: number | null;
  seat_shares?: string | null;
  _isLocal?: boolean;      // true for items not yet confirmed by server
}

export interface LocalOrderState {
  orderHash: string;
  items: LocalOrderItem[];
  /** Approximate sub_total computed locally (server is authoritative) */
  subTotal: number;
  /** Discount applied locally */
  discount: number;
  discountType: number | null;
  discountDescription: string | null;
  /** Promo code applied locally (null when none) */
  promoCode: string | null;
  /** Promotional discount calculated locally */
  promotionalDiscount: number;
  /** Full server-side OrdersWithPayment — updated on reconciliation */
  serverState: any;
  /** True when the sync queue has pending mutations */
  isSyncing: boolean;
}

@Injectable({ providedIn: 'root' })
export class LocalOrderStateService implements OnDestroy {
  private _state = new BehaviorSubject<LocalOrderState | null>(null);
  state$ = this._state.asObservable();
  private _persistSub: Subscription;

  constructor() {
    // Auto-persist snapshots to Dexie on state changes (debounced 300ms)
    this._persistSub = this._state.pipe(
      skip(1),
      debounceTime(300),
    ).subscribe(state => {
      if (state) {
        this._persistSnapshot(state);
      }
    });
  }

  ngOnDestroy(): void {
    this._persistSub.unsubscribe();
  }

  get snapshot(): LocalOrderState | null {
    return this._state.value;
  }

  /**
   * Initialize local state from a server-fetched order.
   * Called on order load and after full reconciliation.
   */
  initFromServer(orderWithPayment: any): void {
    if (!orderWithPayment?.bot_order) return;
    const items: LocalOrderItem[] = (orderWithPayment.items || []).map((item: any) => ({
      id: item.id,
      menu_item: item.menu_item,
      menu_item_raw: item.menu_item_raw,
      quantity: item.quantity,
      modifiers: item.modifiers || [],
      special_request: item.special_request,
      price: item.price,
      is_removed: item.is_removed,
      is_updated: item.is_updated,
      ts_submitted: item.ts_submitted,
      sale_price: item.sale_price,
      portion: item.portion,
      seat: item.seat ?? null,
      seat_shares: item.seat_shares ?? null,
      _isLocal: false,
    }));

    this._state.next({
      orderHash: orderWithPayment.bot_order.order_hash,
      items,
      subTotal: parseFloat(orderWithPayment.payment?.sub_total || '0'),
      discount: parseFloat(orderWithPayment.payment?.instant_discount || '0'),
      discountType: orderWithPayment.bot_order?.instant_discount_type || null,
      discountDescription: orderWithPayment.bot_order?.instant_discount_description || null,
      promoCode: orderWithPayment.bot_order?.promo_code || null,
      promotionalDiscount: parseFloat(orderWithPayment.payment?.promotional_discount || '0'),
      serverState: orderWithPayment,
      isSyncing: false,
    });
  }

  /**
   * Optimistically add an item. Returns the generated temp ID.
   */
  addItem(menuItem: any, quantity: number, modifiers: any[], specialRequest: string, miscModifiers: any[],
          seatFields: { seat?: number | null; seat_shares?: string | number[] | null } = {}): string {
    const state = this._getOrThrow();
    const tempId = `local-${this._uuid()}`;

    // Compute approximate price
    let basePrice = parseFloat(menuItem.base_price || '0');
    let modPrice = 0;
    for (const mod of modifiers) {
      modPrice += parseFloat(mod.upcharge || mod.modifier?.upcharge || '0') * (mod.quantity || 1);
    }
    for (const mm of (miscModifiers || [])) {
      modPrice += parseFloat(mm.upcharge || '0') * (mm.quantity || 1);
    }
    const itemTotal = (basePrice + modPrice) * quantity;

    const newItem: LocalOrderItem = {
      id: tempId,
      menu_item: menuItem,
      menu_item_raw: menuItem.name,
      quantity,
      modifiers: modifiers.map(m => ({
        modifier: m.modifier || { id: m.id, name: m.name },
        mod_type: m.mod_type || 'ADD',
        quantity: m.quantity || 1,
        mod_tag: m.mod_tag || null,
      })),
      special_request: specialRequest,
      price: itemTotal.toFixed(2),
      is_removed: false,
      is_updated: false,
      ts_submitted: false,
      sale_price: null,
      portion: menuItem.portion || '',
      seat: seatFields.seat ?? null,
      seat_shares: Array.isArray(seatFields.seat_shares) ? seatFields.seat_shares.join(',') : (seatFields.seat_shares ?? null),
      _isLocal: true,
    };

    const next = cloneDeep(state);
    next.items.push(newItem);
    next.subTotal += itemTotal;
    this._state.next(next);
    return tempId;
  }

  /**
   * Optimistically update item quantity.
   */
  updateQuantity(itemId: number | string, quantity: number): void {
    const state = this._getOrThrow();
    const next = cloneDeep(state);
    const item = next.items.find(i => i.id === itemId || i.id == itemId);
    if (!item) return;

    const oldPrice = parseFloat(item.price || '0');
    const unitPrice = item.quantity > 0 ? oldPrice / item.quantity : 0;
    item.quantity = quantity;
    item.price = (unitPrice * quantity).toFixed(2);
    next.subTotal = next.items
      .filter(i => !i.is_removed)
      .reduce((sum, i) => sum + parseFloat(i.price || '0'), 0);
    this._state.next(next);
  }

  /**
   * Optimistically void (remove) an item.
   */
  voidItem(itemId: number | string): void {
    const state = this._getOrThrow();
    const next = cloneDeep(state);
    const idx = next.items.findIndex(i => i.id === itemId || i.id == itemId);
    if (idx === -1) return;

    // For local items, just splice out. For server items, mark as removed.
    if (next.items[idx]._isLocal) {
      next.items.splice(idx, 1);
    } else {
      next.items[idx].is_removed = true;
    }

    next.subTotal = next.items
      .filter(i => !i.is_removed)
      .reduce((sum, i) => sum + parseFloat(i.price || '0'), 0);
    this._state.next(next);
  }

  /**
   * Optimistically apply a discount.
   * Updates both local fields AND serverState owp so computeLocalTotals picks it up.
   */
  applyDiscount(type: number, amount: number, description?: string): void {
    const state = this._getOrThrow();
    const next = cloneDeep(state);
    next.discountType = type;
    next.discount = amount;
    next.discountDescription = description || null;

    // Update serverState so computeLocalTotals reads the discount
    if (next.serverState?.bot_order) {
      next.serverState.bot_order.instant_discount_type = type;
      next.serverState.bot_order.instant_discount = amount;
      next.serverState.bot_order.instant_discount_description = description || null;
      OrderHelpers.computeLocalTotals(next.serverState);
    }
    this._state.next(next);
  }

  /**
   * Optimistically remove a discount.
   */
  removeDiscount(): void {
    const state = this._getOrThrow();
    const next = cloneDeep(state);
    next.discountType = null;
    next.discount = 0;
    next.discountDescription = null;

    // Update serverState so computeLocalTotals reads the discount
    if (next.serverState?.bot_order) {
      next.serverState.bot_order.instant_discount_type = null;
      next.serverState.bot_order.instant_discount = null;
      next.serverState.bot_order.instant_discount_description = null;
      OrderHelpers.computeLocalTotals(next.serverState);
    }
    this._state.next(next);
  }

  /**
   * ENG-4644: Optimistically apply a promotional discount (promo code).
   * Updates both local fields AND serverState.payment so computeLocalTotals picks it up.
   */
  applyPromo(promoCode: string, discount: number): void {
    const state = this._getOrThrow();
    const next = cloneDeep(state);
    next.promoCode = promoCode;
    next.promotionalDiscount = discount;

    if (next.serverState?.bot_order) {
      next.serverState.bot_order.promo_code = promoCode;
    }
    if (next.serverState?.payment) {
      next.serverState.payment.promotional_discount = discount;
    }
    OrderHelpers.computeLocalTotals(next.serverState);
    this._state.next(next);
  }

  /**
   * ENG-4644: Optimistically remove a promotional discount.
   */
  removePromo(): void {
    const state = this._getOrThrow();
    const next = cloneDeep(state);
    next.promoCode = null;
    next.promotionalDiscount = 0;

    if (next.serverState?.bot_order) {
      next.serverState.bot_order.promo_code = null;
    }
    if (next.serverState?.payment) {
      next.serverState.payment.promotional_discount = 0;
    }
    OrderHelpers.computeLocalTotals(next.serverState);
    this._state.next(next);
  }

  /**
   * ENG-4644: Optimistically update tip (percent or fixed amount).
   * Updates serverState owp and recomputes all totals locally.
   */
  updateTip(tipPercent: number | null, tipAmount: number | null): void {
    const state = this._getOrThrow();
    const next = cloneDeep(state);
    const owp = next.serverState;
    if (!owp?.bot_order) return;

    if (tipPercent != null) {
      owp.bot_order.tip_percent = tipPercent;
      if (tipPercent === 0) {
        owp.bot_order.tip = 0;
      }
    } else if (tipAmount != null) {
      owp.bot_order.tip_percent = null;
      owp.bot_order.tip = tipAmount;
    }

    OrderHelpers.computeLocalTotals(owp);
    this._state.next(next);
  }

  /**
   * ENG-4644: Optimistically add a misc item. Returns a temp ID.
   */
  addMiscItem(item: { item_name: string; price: number; quantity: number; is_taxable: boolean; seat?: number | null }): string {
    const state = this._getOrThrow();
    const next = cloneDeep(state);
    const owp = next.serverState;
    if (!owp) return '';

    const tempId = `local-misc-${this._uuid()}`;
    if (!owp.misc_items) owp.misc_items = [];
    owp.misc_items.push({
      id: tempId,
      item_name: item.item_name,
      price: item.price,
      quantity: item.quantity,
      is_taxable: item.is_taxable,
      seat: item.seat ?? null,
      seat_shares: null,
      ts_submitted: false,
      _isLocal: true,
    });

    OrderHelpers.computeLocalTotals(owp);
    this._state.next(next);
    return tempId;
  }

  /**
   * ENG-4644: Optimistically delete a misc item.
   */
  deleteMiscItem(miscItemId: number | string): void {
    const state = this._getOrThrow();
    const next = cloneDeep(state);
    const owp = next.serverState;
    if (!owp?.misc_items) return;

    const idx = owp.misc_items.findIndex((m: any) => m.id === miscItemId || m.id == miscItemId);
    if (idx !== -1) {
      owp.misc_items.splice(idx, 1);
    }

    OrderHelpers.computeLocalTotals(owp);
    this._state.next(next);
  }

  /**
   * Seat numbers: move lines to a seat (0 = Table) or share them across seats.
   * Applied to both the item shadow list and the serverState copy the ticket renders.
   */
  setSeat(itemIds: Array<number | string>, miscItemIds: Array<number | string>, seat: number | null, seatShares: number[] | null): void {
    const state = this._getOrThrow();
    const next = cloneDeep(state);
    const shares = seatShares && seatShares.length ? seatShares.join(',') : null;
    const matches = (ids: Array<number | string>, id: any) => ids.some(x => x === id || x == id);
    for (const item of next.items) {
      if (matches(itemIds, item.id)) {
        item.seat = shares ? null : seat;
        item.seat_shares = shares;
      }
    }
    const owp = next.serverState;
    if (owp) {
      for (const item of (owp.items || [])) {
        if (matches(itemIds, item.id)) {
          item.seat = shares ? null : seat;
          item.seat_shares = shares;
        }
      }
      for (const misc of (owp.misc_items || [])) {
        if (matches(miscItemIds, misc.id)) {
          misc.seat = shares ? null : seat;
          misc.seat_shares = shares;
        }
      }
    }
    this._state.next(next);
  }

  /** Seat numbers: how many guests sit at the table. */
  updateGuestCount(guestCount: number): void {
    const state = this._getOrThrow();
    const next = cloneDeep(state);
    const owp = next.serverState;
    if (!owp?.bot_order) { return; }
    owp.bot_order.bot_order_data = { ...(owp.bot_order.bot_order_data || {}), guest_count: guestCount };
    if (owp.seats) { owp.seats.guest_count = guestCount; }
    this._state.next(next);
  }

  /**
   * ENG-4644: Rollback a misc item addition by temp ID.
   */
  rollbackMiscItem(tempId: string): void {
    this.deleteMiscItem(tempId);
  }

  /**
   * ENG-4644: Optimistically update occasion/time/schedule fields.
   * Recomputes totals (delivery_charge depends on occasion).
   */
  updateOccasion(updates: {
    occasion?: string;
    time?: string;
    occasion_schedule?: any;
    delivery_address?: string;
    dine_in_table_number?: string;
    curbside_vehicle_id?: string;
    order_name?: string;
    delivery_fee?: number;
    guest_count?: number | null;
  }): void {
    const state = this._getOrThrow();
    const next = cloneDeep(state);
    const owp = next.serverState;
    if (!owp?.bot_order) return;

    if (updates.occasion != null) {
      owp.bot_order.occasion = updates.occasion;
      owp.bot_order.occasion_raw = updates.occasion;
    }
    if (updates.time != null) {
      owp.bot_order.time = updates.time;
      owp.bot_order.time_raw = updates.time;
    }
    if (updates.occasion_schedule !== undefined) owp.bot_order.occasion_schedule = updates.occasion_schedule;
    if (updates.delivery_address !== undefined) owp.bot_order.delivery_address = updates.delivery_address;
    if (updates.dine_in_table_number !== undefined) owp.bot_order.dine_in_table_number = updates.dine_in_table_number;
    if (updates.curbside_vehicle_id !== undefined) owp.bot_order.curbside_vehicle_id = updates.curbside_vehicle_id;
    if (updates.order_name) owp.bot_order.order_name = updates.order_name;
    if (updates.delivery_fee !== undefined) owp.bot_order.delivery_fee = updates.delivery_fee;
    if (updates.guest_count !== undefined && updates.guest_count !== null) {
      owp.bot_order.bot_order_data = { ...(owp.bot_order.bot_order_data || {}), guest_count: updates.guest_count };
    }

    // Clear delivery fields when switching away from delivery
    if (updates.occasion && updates.occasion !== 'DELIVERY') {
      owp.bot_order.delivery_address = null;
    }
    // Clear table number when switching to pickup
    if (updates.occasion === 'PICKUP') {
      owp.bot_order.dine_in_table_number = null;
    }

    OrderHelpers.computeLocalTotals(owp);
    this._state.next(next);
  }

  /**
   * ENG-4644: Optimistically update customer info (phone, name).
   */
  updateCustomerInfo(phoneNumber?: string, orderName?: string): void {
    const state = this._getOrThrow();
    const next = cloneDeep(state);
    const owp = next.serverState;
    if (!owp?.bot_order) return;

    if (phoneNumber != null) {
      owp.bot_order.phone_number = phoneNumber;
      if (owp.first_user) {
        owp.first_user.phone_number = phoneNumber;
      }
    }
    if (orderName != null) {
      owp.bot_order.order_name = orderName;
    }
    this._state.next(next);
  }

  /**
   * ENG-4644: Optimistically update special request / instruction.
   */
  updateInstruction(specialRequest?: string, orderName?: string): void {
    const state = this._getOrThrow();
    const next = cloneDeep(state);
    const owp = next.serverState;
    if (!owp?.bot_order) return;

    if (specialRequest != null) owp.bot_order.special_request = specialRequest;
    if (orderName != null) owp.bot_order.order_name = orderName;
    this._state.next(next);
  }

  /**
   * ENG-4644: Optimistically toggle tax exempt status.
   */
  updateTaxExempt(isTaxExempt: boolean): void {
    const state = this._getOrThrow();
    const next = cloneDeep(state);
    const owp = next.serverState;
    if (!owp?.bot_order) return;

    owp.bot_order.is_tax_exempt = isTaxExempt;
    OrderHelpers.computeLocalTotals(owp);
    this._state.next(next);
  }

  /**
   * Swap a temp item ID for the real server ID.
   */
  resolveItemId(tempId: string, serverId: number): void {
    this.resolveItemIds([{ tempId, serverId }]);
  }

  /**
   * Batch-resolve multiple temp→server ID mappings in a single state update.
   * Avoids N cloneDeep + N change detection cycles when N mutations are applied.
   */
  resolveItemIds(mappings: Array<{ tempId: string; serverId: number }>): void {
    const state = this._state.value;
    if (!state || mappings.length === 0) return;
    const next = cloneDeep(state);
    for (const { tempId, serverId } of mappings) {
      const item = next.items.find(i => i.id === tempId);
      if (item) {
        item.id = serverId;
        item._isLocal = false;
      }
    }
    this._state.next(next);
  }

  /**
   * Rollback a specific mutation by reverting its effect on local state.
   */
  rollbackAddItem(tempId: string): void {
    const state = this._state.value;
    if (!state) return;
    const next = cloneDeep(state);
    const idx = next.items.findIndex(i => i.id === tempId);
    if (idx !== -1) {
      next.items.splice(idx, 1);
      next.subTotal = next.items
        .filter(i => !i.is_removed)
        .reduce((sum, i) => sum + parseFloat(i.price || '0'), 0);
    }
    this._state.next(next);
  }

  /**
   * Full reconciliation: replace local state with server-computed state.
   * Called after a successful sync batch response.
   */
  reconcileFromServer(orderWithPayment: any): void {
    this.initFromServer(orderWithPayment);
  }

  /**
   * Clear the cached serverState so the next refreshOrder() call fires a real
   * GET /order/ instead of serving the stale local snapshot.
   * Called after send-to-kitchen so ts_submitted values are fetched fresh.
   */
  clearServerState(): void {
    const state = this._state.value;
    if (!state) return;
    this._state.next({ ...state, serverState: null });
  }

  /**
   * B-1: Partial reconciliation — update totals from server without
   * rebuilding the items array.  Used when batch_mutations returns
   * totals_only mode (items=null) to avoid serialization overhead.
   */
  updateTotalsFromServer(orderPayload: any): void {
    const state = this._state.value;
    if (!state) return;
    const payment = orderPayload.payment;
    if (!payment) return;
    const next = {
      ...state,
      subTotal: parseFloat(payment.sub_total || '0'),
      discount: parseFloat(payment.instant_discount || '0'),
      discountType: orderPayload.bot_order?.instant_discount_type || null,
      discountDescription: orderPayload.bot_order?.instant_discount_description || null,
      serverState: { ...state.serverState, payment, bot_order: { ...state.serverState?.bot_order, ...orderPayload.bot_order } },
      isSyncing: false,
    };
    this._state.next(next);
  }

  /** Update syncing indicator */
  setSyncing(syncing: boolean): void {
    const state = this._state.value;
    if (!state) return;
    if (state.isSyncing === syncing) return;
    const next = { ...state, isSyncing: syncing };
    this._state.next(next);
  }

  /**
   * Load order state from Dexie cache (page reload recovery).
   * Returns true if a valid cached snapshot was found and loaded.
   */
  async loadFromCache(orderHash: string): Promise<boolean> {
    try {
      const cached = await posSyncDb.order_snapshots.get(orderHash);
      if (!cached?.state) return false;

      // Don't use stale snapshots (older than 24h)
      if (Date.now() - cached.ts > 24 * 60 * 60 * 1000) {
        await posSyncDb.order_snapshots.delete(orderHash);
        return false;
      }

      this._state.next(cached.state);
      return true;
    } catch (err) {
      return false;
    }
  }

  /**
   * Full cleanup: reset in-memory state and remove Dexie snapshot.
   * Call when an order is cancelled/deleted/paid.
   */
  async clearOrder(orderHash: string): Promise<void> {
    this._state.next(null);
    await this.clearCache(orderHash);
  }

  /**
   * Full reset for store-switch: wipes in-memory state + all Dexie snapshots.
   * Prevents stale order data from a previous store leaking into a new session.
   */
  resetForStoreSwitch(): void {
    this._state.next(null);
    this.clearCache().catch(() => {});
  }

  /**
   * Clear cached snapshot(s) from Dexie.
   * Call when an order is closed/paid to free storage.
   */
  async clearCache(orderHash?: string): Promise<void> {
    try {
      if (orderHash) {
        await posSyncDb.order_snapshots.delete(orderHash);
      } else {
        await posSyncDb.order_snapshots.clear();
      }
    } catch (err) {
      // ignore
    }
  }

  private _persistSnapshot(state: LocalOrderState): void {
    // Sanitize: strip functions (e.g. moment.js ordinal on Store objects)
    // before writing to IndexedDB which cannot clone functions.
    const sanitized = JSON.parse(JSON.stringify(state));
    posSyncDb.order_snapshots.put({
      orderHash: state.orderHash,
      state: sanitized,
      ts: Date.now(),
    }).catch(() => {});
  }

  /** True once initFromServer() has been called for the current order. */
  get isInitialized(): boolean { return !!this._state.value; }

  private _getOrThrow(): LocalOrderState {
    const s = this._state.value;
    if (!s) throw new Error('LocalOrderState not initialized');
    return s;
  }

  private _uuid(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }
}
